'use client'

import { useEffect, useState } from 'react'
import { Route, Routes, useMatch, useNavigate } from 'react-router-dom'
import { FormProvider, useForm } from 'react-hook-form'
import { Spacer, Text } from '@vinted/web-ui'

import {
  businessAccountFromUserDto,
  BusinessAccountStatus,
  BusinessRegistrationModel,
} from '@marketplace-web/business-accounts/business-accounts-data'
import { redirectToPage } from '@marketplace-web/browser/browser-navigation-util'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'

import { ORDERED_REGISTRATION_STEPS, NewRegistrationStep } from '../../constants'
import { ROUTES, MEMBER_PROFILE_URL } from '../../constants/routes'

import InitialDetailsStep from './Form/Dsa/Steps/InitialDetailsStep'
import RepresentativeStep from './Form/Dsa/Steps/RepresentativeStep'

import useUpdateRegistration, { OnValidateRegistrationParams } from './hooks/useUpdateRegistration'
import useDefaultValues from './hooks/useDefaultValues'
import useSubmitRegistration, { OnSubmitRegistrationParams } from './hooks/useSubmitRegistration'

import { BusinessProfile } from './Form'
import { FormNavigation } from './FormNavigation'

const Register = () => {
  const navigate = useNavigate()

  const { user } = useSession()
  const userId = user?.id
  const isBusinessAccount = user?.business
  const businessAccount = businessAccountFromUserDto(user)

  const methods = useForm({
    mode: 'onChange',
  })

  const { defaultValues, onClearDefaultValues } = useDefaultValues()
  const {
    errors: errorUpdate,
    showGeneralError: showGeneralErrorUpdate,
    loading: loadingUpdate,
    onUpdateRegistration,
  } = useUpdateRegistration()
  const {
    errors: errorsSubmit,
    showGeneralError: showGeneralErrorSubmit,
    loading: loadingSubmit,
    onSubmitRegistration,
  } = useSubmitRegistration()

  const errors = errorUpdate || errorsSubmit
  const showGeneralError = showGeneralErrorUpdate || showGeneralErrorSubmit
  const loading = loadingUpdate || loadingSubmit

  const registrationSteps = ORDERED_REGISTRATION_STEPS
  const translate = useTranslate('business.registration')
  const match = useMatch(`${ROUTES.REGISTER.BASE}/:step`)
  const [isContentReady, setIsContentReady] = useState(false)

  const [showFormNavigation, setShowFormNavigation] = useState(false)
  const [registrationData, setRegistrationData] = useState<BusinessRegistrationModel>(defaultValues)

  const stepIndex = registrationSteps.findIndex(orderedStep => match?.params.step === orderedStep)
  const currentStep = stepIndex + 1
  const totalSteps = registrationSteps.length

  const walletConversionStatuses = [
    BusinessAccountStatus.Registered,
    BusinessAccountStatus.WalletConversionInProgress,
  ]
  const isWalletConversionStatus = businessAccount
    ? walletConversionStatuses.includes(businessAccount.status)
    : false

  useEffect(() => {
    if (isBusinessAccount && userId) {
      redirectToPage(MEMBER_PROFILE_URL(userId))

      return
    }

    if (isWalletConversionStatus) {
      navigate(`/${ROUTES.WALLET_CONVERSION}`)

      return
    }

    setIsContentReady(true)

    if (currentStep > 0 && currentStep <= totalSteps) return

    navigate(registrationSteps[0]!)
  }, [
    currentStep,
    totalSteps,
    navigate,
    isWalletConversionStatus,
    isBusinessAccount,
    userId,
    registrationSteps,
  ])

  const handleUpdateRegistration = async ({
    registrationFields,
    onSuccess,
    validate,
  }: OnValidateRegistrationParams) => {
    setRegistrationData(prevData => ({
      ...prevData,
      ...registrationFields,
    }))
    await onUpdateRegistration({
      registrationFields,
      validate,
      onSuccess,
    })
  }

  const handleSubmitRegistration = async ({
    registrationFields,
    onSuccess,
  }: OnSubmitRegistrationParams) => {
    await onSubmitRegistration({
      registrationFields: {
        ...registrationData,
        ...registrationFields,
      },
      onSuccess: () => {
        onSuccess?.()
      },
    })
  }

  if (!isContentReady || !currentStep || currentStep > totalSteps) return null

  return (
    <FormProvider {...methods}>
      <div className="container u-fill-viewport">
        <Spacer size={Spacer.Size.X2Large} />
        <Text
          as="h3"
          type={Text.Type.Subtitle}
          text={translate('step', { currentStep, totalSteps })}
        />
        <Spacer />
        <Routes>
          <Route
            path={NewRegistrationStep.BusinessDetails}
            element={
              <InitialDetailsStep
                showFullForm={showFormNavigation}
                onFullFormShow={(show: boolean) => setShowFormNavigation(show)}
                showGeneralError={showGeneralError}
                onUpdateRegistration={onUpdateRegistration}
                apiErrors={errors}
                defaultValues={defaultValues}
                onClearDefaultValues={onClearDefaultValues}
              />
            }
          />
          <Route
            path={NewRegistrationStep.BusinessRepresentative}
            element={
              <RepresentativeStep
                showGeneralError={showGeneralError}
                apiErrors={errors}
                defaultValues={defaultValues}
              />
            }
          />
          <Route
            path={NewRegistrationStep.BusinessProfile}
            element={
              <BusinessProfile
                showGeneralError={showGeneralError}
                apiErrors={errors}
                defaultValues={defaultValues}
              />
            }
          />
        </Routes>
        <Spacer size={Spacer.Size.X2Large} />
      </div>
      {showFormNavigation && (
        <div className="bottom-navigation-footer">
          <FormNavigation
            loading={loading}
            stepIndex={stepIndex}
            onBackButtonClick={() => (stepIndex === 1 ? setShowFormNavigation(false) : null)}
            onUpdateRegistration={handleUpdateRegistration}
            onSubmitRegistration={handleSubmitRegistration}
          />
        </div>
      )}
    </FormProvider>
  )
}

export default Register
